import React, { useState } from 'react';
import {
  Upload,
  Image,
  Video,
  Crop,
  Youtube,
  Link2,
  AlertCircle,
  CheckCircle,
  Trash2,
} from 'lucide-react';
import { ImageCropModal } from '../shared/ImageCropModal';
import type { CropData } from '../shared/ImageCropModal';
import type { Area } from 'react-easy-crop';
import { getCroppedImageRenderProps, hasCropApplied } from '../shared/cropUtils';
import {
  getEmbedSrc,
  isEmbedProvider,
  parseVimeoUrl,
  parseYouTubeUrl,
  type VideoSourceType,
} from '../../../utils/videoEmbed';

interface MediaData {
  type?: 'image' | 'video';
  url?: string;
  sourceType?: VideoSourceType;
  embedUrl?: string;
  cloudfrontUrl?: string;
  mediaId?: string;
  alt?: string;
  poster?: string;
  aspectRatio?: string;
  borderRadius?: string;
  crop?: CropData;
  thumbnail?: {
    url: string;
    cloudfrontUrl?: string;
    mediaId?: string;
    alt?: string;
  };
}

interface MediaTextData {
  media?: MediaData;
  [key: string]: any;
}

interface MediaPreviewProps {
  data: MediaTextData;
  setFormData: (data: MediaTextData) => void;
  openMediaUpload: () => void;
}

export const MediaPreview: React.FC<MediaPreviewProps> = ({
  data,
  setFormData,
  openMediaUpload,
}) => {
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [embedUrlInput, setEmbedUrlInput] = useState(data.media?.embedUrl || '');
  const [embedError, setEmbedError] = useState<string | null>(null);

  const displayUrl = data.media?.cloudfrontUrl || data.media?.url;
  const isImage = data.media?.type !== 'video';
  const isVideo = data.media?.type === 'video';
  const sourceType = data.media?.sourceType || 'upload';

  // For backward compatibility: check if we have an uploaded video
  const hasUploadedVideo =
    isVideo && data.media?.url && (sourceType === 'upload' || !data.media?.sourceType);
  const isEmbed = isVideo && (sourceType === 'youtube' || sourceType === 'vimeo');

  const handleCropApply = (cropData: CropData, _croppedAreaPixels: Area) => {
    setFormData({
      ...data,
      media: { ...data.media, crop: cropData },
    });
    setIsCropModalOpen(false);
  };

  const handleRemoveMedia = () => {
    setFormData({
      ...data,
      media: {
        ...data.media,
        url: '',
        embedUrl: undefined,
        cloudfrontUrl: undefined,
        mediaId: undefined,
        crop: undefined,
      },
    });
    setEmbedUrlInput('');
    setEmbedError(null);
  };

  const handleSourceTypeChange = (newType: VideoSourceType) => {
    setFormData({
      ...data,
      media: {
        ...data.media,
        sourceType: newType,
        url: '',
        embedUrl: undefined,
        cloudfrontUrl: undefined,
        mediaId: undefined,
      },
    });
    setEmbedUrlInput('');
    setEmbedError(null);
  };

  const handleEmbedUrlChange = (url: string) => {
    setEmbedUrlInput(url);
    setEmbedError(null);

    if (!url.trim()) {
      setFormData({
        ...data,
        media: {
          ...data.media,
          sourceType,
          url: '',
          embedUrl: undefined,
        },
      });
      return;
    }

    // Parse the URL based on source type
    let videoId: string | null = null;

    if (sourceType === 'youtube') {
      videoId = parseYouTubeUrl(url);
      if (!videoId) {
        setEmbedError('Invalid YouTube URL. Please use a valid YouTube video link.');
        return;
      }
    } else if (sourceType === 'vimeo') {
      videoId = parseVimeoUrl(url);
      if (!videoId) {
        setEmbedError('Invalid Vimeo URL. Please use a valid Vimeo video link.');
        return;
      }
    }

    if (videoId) {
      setFormData({
        ...data,
        media: {
          ...data.media,
          sourceType,
          url: videoId,
          embedUrl: url,
        },
      });
    }
  };

  // Get display info for embed videos
  const getEmbedDisplayInfo = () => {
    if (isEmbedProvider(sourceType) && data.media?.url) {
      const previewUrl = getEmbedSrc(sourceType, data.media.url);
      if (!previewUrl) return null;
      return {
        icon:
          sourceType === 'youtube' ? (
            <Youtube size={16} className="text-red-600" />
          ) : (
            <Video size={16} className="text-blue-500" />
          ),
        label: sourceType === 'youtube' ? 'YouTube Video' : 'Vimeo Video',
        previewUrl,
      };
    }
    return null;
  };

  const embedInfo = getEmbedDisplayInfo();

  // Render for images (unchanged logic)
  if (isImage && data.media?.url) {
    return (
      <>
        <div className="space-y-3">
          <div className="relative border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50 group">
            {(() => {
              const cropPreview = getCroppedImageRenderProps(data.media.crop);
              const useCropBox = Boolean(cropPreview.hasCrop && cropPreview.aspectRatio);
              return (
                <div
                  className={`relative w-full overflow-hidden ${useCropBox ? '' : 'h-48'}`}
                  style={useCropBox ? { aspectRatio: cropPreview.aspectRatio } : undefined}
                >
                  {data.media.crop?.croppedAreaPercent ? (
                    <img
                      src={data.media.cloudfrontUrl || data.media.url}
                      alt={data.media.alt || 'Media preview'}
                      style={cropPreview.imageStyle}
                    />
                  ) : (
                    <img
                      src={data.media.cloudfrontUrl || data.media.url}
                      alt={data.media.alt || 'Media preview'}
                      className="w-full h-full object-cover"
                      style={{
                        filter: data.media.crop?.grayscale ? 'grayscale(100%)' : undefined,
                      }}
                    />
                  )}
                  {hasCropApplied(data.media.crop) && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center shadow-sm z-10">
                      <Crop size={14} className="text-white" />
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setIsCropModalOpen(true)}
                  className="px-4 py-2 bg-white text-gray-700 rounded-lg shadow-lg hover:bg-gray-50 flex items-center space-x-2 text-sm font-medium"
                >
                  <Crop size={16} />
                  <span>Crop</span>
                </button>
                <button
                  type="button"
                  onClick={openMediaUpload}
                  className="px-4 py-2 bg-white text-gray-700 rounded-lg shadow-lg hover:bg-gray-50 flex items-center space-x-2 text-sm font-medium"
                >
                  <Upload size={16} />
                  <span>Replace</span>
                </button>
                <button
                  type="button"
                  onClick={handleRemoveMedia}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg shadow-lg hover:bg-red-700 flex items-center space-x-2 text-sm font-medium"
                >
                  <span>Remove</span>
                </button>
              </div>
            </div>
          </div>

          <div className="text-sm text-gray-600 flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
            <span className="flex items-center space-x-2">
              <Image size={16} className="text-blue-600" />
              <span className="font-medium">Image content</span>
            </span>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => setIsCropModalOpen(true)}
                className="text-primary-600 hover:text-primary-700 font-medium hover:underline flex items-center space-x-1"
              >
                <Crop size={14} />
                <span>Crop</span>
              </button>
              <button
                type="button"
                onClick={openMediaUpload}
                className="text-blue-600 hover:text-blue-700 font-medium hover:underline"
              >
                Change image
              </button>
            </div>
          </div>
        </div>

        {displayUrl && (
          <ImageCropModal
            isOpen={isCropModalOpen}
            onClose={() => setIsCropModalOpen(false)}
            imageUrl={displayUrl}
            initialCrop={data.media.crop}
            onApply={handleCropApply}
          />
        )}
      </>
    );
  }

  // Render for video type
  if (isVideo) {
    return (
      <div className="space-y-4">
        {/* Video Source Type Selector */}
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => handleSourceTypeChange('upload')}
            className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 transition-all text-sm ${
              sourceType === 'upload'
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-200 hover:border-gray-300 text-gray-600'
            }`}
          >
            <Upload size={16} />
            <span className="font-medium">Upload</span>
          </button>
          <button
            type="button"
            onClick={() => handleSourceTypeChange('youtube')}
            className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 transition-all text-sm ${
              sourceType === 'youtube'
                ? 'border-red-500 bg-red-50 text-red-700'
                : 'border-gray-200 hover:border-gray-300 text-gray-600'
            }`}
          >
            <Youtube size={16} />
            <span className="font-medium">YouTube</span>
          </button>
          <button
            type="button"
            onClick={() => handleSourceTypeChange('vimeo')}
            className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 transition-all text-sm ${
              sourceType === 'vimeo'
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-200 hover:border-gray-300 text-gray-600'
            }`}
          >
            <Video size={16} />
            <span className="font-medium">Vimeo</span>
          </button>
        </div>

        {/* Upload Video */}
        {sourceType === 'upload' && (
          <>
            {hasUploadedVideo ? (
              <div className="space-y-3">
                <div className="relative border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50 group">
                  <div className="aspect-video">
                    <video
                      src={data.media?.cloudfrontUrl || data.media?.url}
                      className="w-full h-full object-cover"
                      controls
                      poster={
                        data.media?.thumbnail?.cloudfrontUrl ||
                        data.media?.thumbnail?.url ||
                        data.media?.poster
                      }
                    />
                  </div>
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={openMediaUpload}
                        className="px-4 py-2 bg-white text-gray-700 rounded-lg shadow-lg hover:bg-gray-50 flex items-center space-x-2 text-sm font-medium"
                      >
                        <Upload size={16} />
                        <span>Replace</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleRemoveMedia}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg shadow-lg hover:bg-red-700 flex items-center space-x-2 text-sm font-medium"
                      >
                        <Trash2 size={16} />
                        <span>Remove</span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-600 flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                  <span className="flex items-center space-x-2">
                    <Video size={16} className="text-purple-600" />
                    <span className="font-medium">Uploaded video</span>
                  </span>
                  <button
                    type="button"
                    onClick={openMediaUpload}
                    className="text-blue-600 hover:text-blue-700 font-medium hover:underline"
                  >
                    Change video
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-400 hover:bg-primary-50 transition-all duration-200 cursor-pointer group"
                onClick={openMediaUpload}
              >
                <div className="flex flex-col items-center space-y-3">
                  <div className="p-3 bg-gray-100 group-hover:bg-primary-100 rounded-full transition-colors">
                    <Video size={24} className="text-gray-600 group-hover:text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 group-hover:text-primary-700">
                      Upload Video
                    </p>
                    <p className="text-xs text-gray-400 mt-1">MP4, WebM, or OGG up to 50MB</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* YouTube/Vimeo Embed */}
        {(sourceType === 'youtube' || sourceType === 'vimeo') && (
          <div className="space-y-3">
            {/* URL Input */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Link2 size={16} className="text-gray-400" />
              </div>
              <input
                type="text"
                value={embedUrlInput}
                onChange={e => handleEmbedUrlChange(e.target.value)}
                className={`w-full pl-9 pr-9 py-2.5 border rounded-lg focus:ring-2 focus:ring-offset-0 transition-colors text-sm ${
                  embedError
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : data.media?.url && isEmbed
                      ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                      : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
                }`}
                placeholder={
                  sourceType === 'youtube'
                    ? 'https://youtube.com/watch?v=... or youtu.be/...'
                    : 'https://vimeo.com/123456789'
                }
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                {embedError && <AlertCircle size={16} className="text-red-500" />}
                {!embedError && data.media?.url && isEmbed && (
                  <CheckCircle size={16} className="text-green-500" />
                )}
              </div>
            </div>

            {embedError && <p className="text-xs text-red-600">{embedError}</p>}

            {!embedError && !data.media?.url && (
              <p className="text-xs text-gray-500">
                {sourceType === 'youtube' ? 'Paste a YouTube video URL' : 'Paste a Vimeo video URL'}
              </p>
            )}

            {/* Embed Preview */}
            {!embedError && data.media?.url && isEmbed && embedInfo && (
              <div className="space-y-3">
                <div className="relative border-2 border-gray-200 rounded-lg overflow-hidden bg-black group">
                  <div className="aspect-video">
                    <iframe
                      src={embedInfo.previewUrl}
                      className="w-full h-full"
                      style={{ border: 0 }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="Video preview"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={handleRemoveMedia}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg shadow-lg hover:bg-red-700 flex items-center space-x-2 text-sm font-medium"
                    >
                      <Trash2 size={16} />
                      <span>Remove</span>
                    </button>
                  </div>
                </div>
                <div className="text-sm text-gray-600 flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                  <span className="flex items-center space-x-2">
                    {embedInfo.icon}
                    <span className="font-medium">{embedInfo.label}</span>
                    <span className="text-gray-400 text-xs">ID: {data.media.url}</span>
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Default: Upload placeholder for images
  return (
    <div
      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-400 hover:bg-primary-50 transition-all duration-200 cursor-pointer group"
      onClick={openMediaUpload}
    >
      <div className="flex flex-col items-center space-y-3">
        <div className="p-3 bg-gray-100 group-hover:bg-primary-100 rounded-full transition-colors">
          <Image size={24} className="text-gray-600 group-hover:text-primary-600" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-700 group-hover:text-primary-700">
            Upload image
          </p>
          <p className="text-xs text-gray-400 mt-1">PNG, JPG, or WebP up to 10MB</p>
        </div>
      </div>
    </div>
  );
};

export type { MediaData };
